import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import type { UserIdentityAdmin } from "@chotca/config";
import { useAdminIdentityList } from "@chotca/api-client";
import {
  DataTable,
  usePagination,
  type Column,
  type RowAction,
} from "@/components/data-table";

// Admin review queue for account identity (CCCD) — SCRUM-71. Keyed by userId
// (one identity per account), unlike the submissionId-keyed KYC queues.
export function IdentityListClient() {
  const nav = useNavigate();
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { data, isLoading, isFetching } = useAdminIdentityList({
    page,
    limit: pageSize,
  });
  const items = (data?.items ?? []) as UserIdentityAdmin[];

  const columns: Column<UserIdentityAdmin>[] = [
    {
      key: "fullName",
      header: "Họ tên",
      cell: (s) => <span className="font-semibold text-ink">{s.fullName}</span>,
    },
    {
      key: "phone",
      header: "Phone",
      cell: (s) => <span className="text-xs">{s.userPhone ?? "—"}</span>,
    },
    {
      key: "email",
      header: "Email",
      cell: (s) => (
        <span className="text-xs text-ash">{s.userEmail ?? "—"}</span>
      ),
    },
    {
      key: "submittedAt",
      header: "Nộp lúc",
      cell: (s) => (
        <span className="whitespace-nowrap text-xs text-ash">
          {new Date(s.submittedAt).toLocaleString("vi-VN")}
        </span>
      ),
    },
  ];

  const rowActions: RowAction<UserIdentityAdmin>[] = [
    {
      key: "view",
      label: "Xem chi tiết",
      icon: Eye,
      onClick: (s) => nav(`/kyc/identity/${s.userId}`),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">Định danh tài khoản</h1>
      </header>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(s) => s.userId}
        rowActions={rowActions}
        onRowClick={(s) => nav(`/kyc/identity/${s.userId}`)}
        emptyText="Không có hồ sơ định danh chờ duyệt."
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
